/**
 * useTextSelection - Hook for capturing and persisting text selection in text components
 *
 * This hook solves the problem where browser's native text selection is lost
 * when focus moves to another element (like the AI chat input).
 *
 * It captures selection events and stores them in the editor store,
 * allowing the selection to persist and be rendered with custom highlighting.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useEditorStore } from '@/stores/editor/editor-store';
import type { TextSelectionContext } from '@/types/ai';

interface UseTextSelectionOptions {
  /** The component ID this text belongs to */
  componentId: string;
  /** The full text content of the component */
  fullText: string;
  /** Whether text selection is enabled (typically only in editor mode) */
  enabled?: boolean;
}

interface UseTextSelectionResult {
  /** Ref to attach to the text container element */
  textRef: React.RefObject<HTMLDivElement>;
  /** Current stored text selection (may differ from browser's native selection) */
  storedSelection: TextSelectionContext | null;
  /** Clear the stored selection */
  clearSelection: () => void;
  /** Check if this component has an active selection */
  hasSelection: boolean;
}

export function useTextSelection({
  componentId,
  fullText: _fullText, // Prefixed with underscore - not used directly but required in interface for type safety
  enabled = true,
}: UseTextSelectionOptions): UseTextSelectionResult {
  const textRef = useRef<HTMLDivElement>(null);
  const isSelectingRef = useRef(false); // Track if user is currently selecting (mouse down)

  const textSelection = useEditorStore(state => state.textSelection);
  const setTextSelection = useEditorStore(state => state.setTextSelection);
  const clearTextSelection = useEditorStore(state => state.clearTextSelection);

  // Persist the custom highlight whenever this component owns the stored selection.
  // Tying it to canvas selection causes the highlight to disappear when wrapper
  // click handling and text selection timing drift slightly apart.
  const hasSelection = textSelection?.componentId === componentId &&
                       textSelection.startIndex !== textSelection.endIndex;

  // Capture text selection whenever it changes
  const captureSelection = useCallback(() => {
    if (!enabled || !textRef.current) {
      console.log('[useTextSelection] captureSelection skipped', {
        componentId,
        enabled,
        hasTextRef: Boolean(textRef.current),
      });
      return;
    }

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      console.log('[useTextSelection] captureSelection skipped: no browser selection', {
        componentId,
      });
      return;
    }

    const range = selection.getRangeAt(0);

    // Check if the selection is within our text element
    if (!textRef.current.contains(range.commonAncestorContainer)) {
      console.log('[useTextSelection] captureSelection skipped: selection outside text node', {
        componentId,
        selectedText: selection.toString(),
      });
      return;
    }

    // Skip if selection is collapsed (just a cursor)
    if (selection.isCollapsed) {
      console.log('[useTextSelection] captureSelection skipped: collapsed selection', {
        componentId,
      });
      return;
    }

    const selectedText = selection.toString();
    if (!selectedText.trim()) {
      console.log('[useTextSelection] captureSelection skipped: empty selected text', {
        componentId,
      });
      return;
    }

    // Calculate the start and end indices relative to the full text
    const textContent = textRef.current.textContent || '';
    const startOffset = getTextOffset(textRef.current, range.startContainer, range.startOffset);
    const endOffset = getTextOffset(textRef.current, range.endContainer, range.endOffset);

    if (startOffset === -1 || endOffset === -1) {
      console.warn('[useTextSelection] Could not calculate text offsets');
      return;
    }

    const selectionContext: TextSelectionContext = {
      startIndex: Math.min(startOffset, endOffset),
      endIndex: Math.max(startOffset, endOffset),
      selectedText,
      fullText: textContent,
      isCursor: false,
      componentId,
    };

    console.log('[useTextSelection] Text selection captured:', selectionContext);
    setTextSelection(selectionContext);
  }, [enabled, componentId, setTextSelection]);

  // Listen for selection changes on the document
  useEffect(() => {
    if (!enabled) return;

    // Track mousedown to know when user is actively selecting
    const handleMouseDown = (e: MouseEvent) => {
      if (textRef.current && textRef.current.contains(e.target as Node)) {
        console.log('[useTextSelection] mousedown inside text', {
          componentId,
          target: (e.target as HTMLElement)?.tagName,
          existingStoredSelection: textSelection,
        });
        isSelectingRef.current = true;
        // Clear any existing stored selection when starting a new selection
        // This prevents the old custom highlight from interfering with drag selection
        if (textSelection?.componentId === componentId) {
          console.log('[useTextSelection] clearing previous stored selection on new drag', {
            componentId,
            textSelection,
          });
          clearTextSelection();
        }
      }
    };

    // Capture selection ONLY on mouseup to avoid interfering with drag selection
    // During drag, the browser's native selection works normally
    // After mouseup, we capture the final selection and show our custom highlight
    const handleMouseUp = (e: MouseEvent) => {
      const eventTarget = e.target as Node | null;
      const selectionStartedInThisText = isSelectingRef.current;

      // Mark that selection is complete
      isSelectingRef.current = false;

      if (!textRef.current) {
        return;
      }

      // Check if there's a valid selection within the text element
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) {
        return;
      }

      const range = selection.getRangeAt(0);
      const selectionIsWithinText = textRef.current.contains(range.commonAncestorContainer);
      const eventIsWithinText = eventTarget ? textRef.current.contains(eventTarget) : false;

      if (!selectionStartedInThisText && !selectionIsWithinText && !eventIsWithinText) {
        return;
      }

      // Only process if selection is within our text element
      if (!selectionIsWithinText) {
        return;
      }

      // Skip if selection is collapsed (just a cursor with no selection)
      if (selection.isCollapsed) {
        return;
      }

      console.log('[useTextSelection] mouseup scheduling capture', {
        componentId,
        selectedText: selection.toString(),
      });

      // Small delay to ensure selection is finalized, then capture
      setTimeout(() => {
        captureSelection();
      }, 10);
    };

    // Handle double-click for word selection
    const handleDoubleClick = (e: MouseEvent) => {
      // Double-click is handled natively, we just capture the result
      if (!textRef.current || !textRef.current.contains(e.target as Node)) {
        return;
      }

      console.log('[useTextSelection] dblclick scheduling capture', {
        componentId,
        browserSelection: window.getSelection()?.toString(),
      });

      // Double-click selects a word - capture it after a small delay
      setTimeout(() => {
        captureSelection();
      }, 10);
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('dblclick', handleDoubleClick);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('dblclick', handleDoubleClick);
    };
  }, [enabled, captureSelection, textSelection, componentId, clearTextSelection]);

  return {
    textRef,
    storedSelection: hasSelection ? textSelection : null,
    clearSelection: clearTextSelection,
    hasSelection,
  };
}

/**
 * Calculate the text offset (character index) from the start of the container
 * to a specific position within a text node.
 */
function getTextOffset(
  container: Node,
  targetNode: Node,
  targetOffset: number
): number {
  let offset = 0;

  const walker = document.createTreeWalker(
    container,
    NodeFilter.SHOW_TEXT,
    null
  );

  let node = walker.nextNode();
  while (node) {
    if (node === targetNode) {
      return offset + targetOffset;
    }
    offset += node.textContent?.length || 0;
    node = walker.nextNode();
  }

  // Target node not found in container
  return -1;
}

export default useTextSelection;
